import type { AutoReplyProcessingResult, ReviewAnswerDraftStatus } from '@/contracts/marketplace/auto-replies';
import { ConvertedReviewStatus, convertedReviewStatusStrings } from '@/enums/converted-review-status';
import {
    getAutoReplyProcessingResultLabel,
    getReviewAnswerDraftStatusLabel,
    type AutoReplyLabelLocale,
} from '@/shared/auto-replies-labels';

export const REVIEW_PENDING_APPROVAL_STATUS = 'pending_approval' as const;
export const REVIEW_AUTO_REPLY_PROCESSING_STATUS = 'auto_reply_processing' as const;

export const CONVERTED_REVIEW_STATUS_FILTER_VALUES = [
    ConvertedReviewStatus.New,
    ConvertedReviewStatus.Answered,
    ConvertedReviewStatus.AnswerChanged,
    ConvertedReviewStatus.Augmented,
    ConvertedReviewStatus.AnswerRejected,
] as const;

export const REVIEW_STATUS_FILTER_VALUES = [
    ...CONVERTED_REVIEW_STATUS_FILTER_VALUES,
    REVIEW_PENDING_APPROVAL_STATUS,
    REVIEW_AUTO_REPLY_PROCESSING_STATUS,
] as const;

export type ReviewStatusFilterValue = (typeof REVIEW_STATUS_FILTER_VALUES)[number];

const convertedReviewStatusLabels: Record<AutoReplyLabelLocale, Record<ConvertedReviewStatus, string>> = {
    ru: convertedReviewStatusStrings,
    en: {
        [ConvertedReviewStatus.New]: 'New',
        [ConvertedReviewStatus.Answered]: 'Answered',
        [ConvertedReviewStatus.AnswerChanged]: 'Answer changed',
        [ConvertedReviewStatus.Augmented]: 'Augmented',
        [ConvertedReviewStatus.AnswerRejected]: 'Answer rejected',
    },
};

export const isConvertedReviewStatusFilterValue = (value: string): value is ConvertedReviewStatus =>
    CONVERTED_REVIEW_STATUS_FILTER_VALUES.includes(value as ConvertedReviewStatus);

export const isReviewPendingApprovalStatus = (
    value: string | undefined,
): value is typeof REVIEW_PENDING_APPROVAL_STATUS => value === REVIEW_PENDING_APPROVAL_STATUS;

export const isReviewAutoReplyProcessingStatus = (
    value: string | undefined,
): value is typeof REVIEW_AUTO_REPLY_PROCESSING_STATUS => value === REVIEW_AUTO_REPLY_PROCESSING_STATUS;

export const isReviewStatusFilterValue = (value: string): value is ReviewStatusFilterValue =>
    isConvertedReviewStatusFilterValue(value) ||
    isReviewPendingApprovalStatus(value) ||
    isReviewAutoReplyProcessingStatus(value);

export const getReviewStatusFilterLabel = (
    value: ReviewStatusFilterValue,
    locale: AutoReplyLabelLocale = 'ru',
): string => {
    if (value === REVIEW_PENDING_APPROVAL_STATUS) {
        return getReviewAnswerDraftStatusLabel(value, locale);
    }

    if (value === REVIEW_AUTO_REPLY_PROCESSING_STATUS) {
        return getAutoReplyProcessingResultLabel('processing', locale);
    }

    return convertedReviewStatusLabels[locale]?.[value] ?? convertedReviewStatusLabels.ru[value];
};

export const resolveReviewStatusDisplayValue = ({
    convertedStatus,
    pendingAnswerDraft,
    autoReplyProcessing,
}: {
    convertedStatus: ConvertedReviewStatus;
    pendingAnswerDraft?: { status?: ReviewAnswerDraftStatus | null } | null;
    autoReplyProcessing?: { result?: AutoReplyProcessingResult | null } | null;
}): ReviewStatusFilterValue => {
    if (pendingAnswerDraft?.status === REVIEW_PENDING_APPROVAL_STATUS) {
        return REVIEW_PENDING_APPROVAL_STATUS;
    }

    if (autoReplyProcessing?.result === 'processing') {
        return REVIEW_AUTO_REPLY_PROCESSING_STATUS;
    }

    return convertedStatus;
};
